import { useEffect, useState } from 'react';
import { Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import {
  Navigate,
  Outlet,
  RouteObject,
  useMatch,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { PageHeader, PageLayout } from 'shared/layout/PageLayout';
import { useBypassNavigationPrompt } from 'shared/routing/NavigationBlock';
import { useFlag } from 'shared/settings/FeatureToggles';
import { BackButton } from 'shared/ui/BackButton';
import styled from 'styled-components';
import {
  mapVehiclesSetupToSubmitPayload,
  useCarrierEDIIntegration,
  useSubmitCarrierEDIIntegration,
} from './data/VehiclesAPI';
import {
  VehiclesSetupCompanyInfoDTO,
  VehiclesSetupConnectionDetailsDTO,
} from './data/VehiclesDTO';
import { trackVehiclesEvent } from './data/VehiclesAnalytics';
import { VehiclesSetupCompanyInfoStep } from './core/VehiclesSetupCompanyInfoStep';
import { VehiclesSetupConnectionDetailsStep } from './core/VehiclesSetupConnectionDetailsStep';
import { VehiclesSetupStepper } from './core/VehiclesSetupStepper';

interface VehiclesSetupWizardContext {
  companyInfo: undefined | VehiclesSetupCompanyInfoDTO;
  setCompanyInfo: (values: VehiclesSetupCompanyInfoDTO) => void;
  connectionDetails: undefined | VehiclesSetupConnectionDetailsDTO;
  setConnectionDetails: (values: VehiclesSetupConnectionDetailsDTO) => void;
}

function useWizardContext() {
  return useOutletContext<VehiclesSetupWizardContext>();
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  padding-top: 32px;
  justify-content: center;
`;

export function VehiclesSetupWizardOutlet() {
  const navigate = useNavigate();
  const isSelfServeSetupEnabled = useFlag('ctms_edi_setup_vehicles');
  const {
    data: carrierEDIIntegration,
    isLoading,
    isError,
  } = useCarrierEDIIntegration();
  const isConnectionDetailsStep = !!useMatch(
    '/vehicles/setup/connection-details',
  );
  const [companyInfo, setCompanyInfo] = useState<VehiclesSetupCompanyInfoDTO>();
  const [connectionDetails, setConnectionDetails] =
    useState<VehiclesSetupConnectionDetailsDTO>();

  const isSetupComplete =
    carrierEDIIntegration?.is_edi_enabled ||
    carrierEDIIntegration?.setup_status === 'completed';
  const isPending = carrierEDIIntegration?.setup_status === 'pending';

  useEffect(() => {
    if (
      !isLoading &&
      (!isSelfServeSetupEnabled || isError || isSetupComplete || isPending)
    ) {
      navigate('/vehicles', { replace: true });
    }
  }, [
    isLoading,
    isSelfServeSetupEnabled,
    isError,
    isSetupComplete,
    isPending,
    navigate,
  ]);

  // Guards a direct link/refresh landing on Step 2 without Step 1's values
  // in memory (they're only kept in local state, not persisted).
  useEffect(() => {
    if (isConnectionDetailsStep && !companyInfo) {
      navigate('/vehicles/setup/company-info', { replace: true });
    }
  }, [isConnectionDetailsStep, companyInfo, navigate]);

  return (
    <PageLayout
      background="White"
      header={
        <PageHeader
          title="Setup Vehicles"
          startAction={
            <BackButton
              label="Back"
              onClick={() => {
                navigate('/vehicles');
              }}
            />
          }
        />
      }
    >
      <Container>
        <Box maxWidth="528px" width="100%">
          <Stack space="large">
            <VehiclesSetupStepper step={isConnectionDetailsStep ? 2 : 1} />
            <Outlet
              context={{
                companyInfo,
                setCompanyInfo,
                connectionDetails,
                setConnectionDetails,
              }}
            />
          </Stack>
        </Box>
      </Container>
    </PageLayout>
  );
}

function CompanyInfoStepRoute() {
  // The step's own <Prompt> can still see stale `formik.status` right after
  // a successful submit — Formik updates it in a microtask that hasn't run
  // yet when this synchronously navigates away. Bypass it here since the
  // values are already cached in the wizard's state above.
  const navigate = useBypassNavigationPrompt();
  const { companyInfo, setCompanyInfo } = useWizardContext();

  return (
    <VehiclesSetupCompanyInfoStep
      savedValues={companyInfo}
      onContinue={(values) => {
        setCompanyInfo(values);
        trackVehiclesEvent({ name: 'Carrier Completed Vehicle Setup Step 1' });
        navigate('/vehicles/setup/connection-details');
      }}
    />
  );
}

function ConnectionDetailsStepRoute() {
  // Same stale-`Prompt` race as the Company Info step — see the comment
  // there.
  const navigate = useBypassNavigationPrompt();
  const { companyInfo, connectionDetails, setConnectionDetails } =
    useWizardContext();
  const { mutateAsync: submitCarrierEDIIntegration, isSuccess } =
    useSubmitCarrierEDIIntegration();

  // Navigating away as part of the same promise chain Formik awaits would
  // race the unsaved-changes `Prompt` — it reads Formik's status from the
  // last committed render, which hasn't flipped to "submitted" yet at that
  // point, so it would block the very navigation the successful submit
  // triggers. Driving it from `isSuccess` instead defers to React's normal
  // commit-then-effect ordering, so the render marking the form as
  // submitted always lands before this navigates away.
  useEffect(() => {
    if (isSuccess) {
      navigate('/vehicles', { replace: true });
    }
  }, [isSuccess, navigate]);

  return (
    <VehiclesSetupConnectionDetailsStep
      savedValues={connectionDetails}
      onBack={(values) => {
        setConnectionDetails(values);
        navigate('/vehicles/setup/company-info');
      }}
      onComplete={(values) => {
        if (!companyInfo) {
          // Shouldn't happen — the wizard outlet redirects back to Step 1
          // whenever Step 2 is reached without Step 1's values in memory.
          return Promise.reject(new Error('Missing company information'));
        }

        return submitCarrierEDIIntegration(
          mapVehiclesSetupToSubmitPayload(companyInfo, values),
        ).then((response) => {
          trackVehiclesEvent({
            name: 'Carrier Completed Vehicle Setup Step 2',
          });
          return response;
        });
      }}
    />
  );
}

export const vehiclesSetupRoutes: RouteObject[] = [
  {
    path: 'setup',
    element: <VehiclesSetupWizardOutlet />,
    children: [
      { index: true, element: <Navigate to="company-info" replace={true} /> },
      { path: 'company-info', element: <CompanyInfoStepRoute /> },
      { path: 'connection-details', element: <ConnectionDetailsStepRoute /> },
    ],
  },
];
